import { openWindow } from '@common/oAuth/utils';
import { useReplayModeType } from '@common/Replay/utils';
import { Private_Routes } from '@common/url';
import { getRedirectUri } from '@common/utils/misc/redirect';
import { Callable } from '@prophecy/interfaces/generic';
import { theme } from '@prophecy/theme';
import { toast } from '@prophecy/ui-v3/Toast';
import { roundToDecimals } from '@prophecy/utils/number';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useGlobalVariables } from 'frontend/core/src/context/globalConfig/securedConfigHook';
import { useUser } from 'frontend/core/src/context/user';
import { useRestQuery } from 'frontend/core/src/data/util';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { BlockedReason } from './BlockedReason';
import { DEFAULT_PROFESSIONAL_METERING_INFO } from './constants';
import { getProfessionalMeteringInfo } from './data/api';
import { PLAN_TYPES, ProfessionalMeteringInfo } from './types';
import { useProfessionalEditionCreditsContext } from './useProfessionalEditionCreditsContext';

interface ProfessionalCreditUsage {
  isOutOfCredits: boolean;
  isLowOnCredits: boolean;
  creditsConsumed: number;
  totalCreditsAllocated: number;
  creditsRemaining: number;
  usagePercentage: number;
  usageAlertCredits: number;
  usageBudgetCredits: number;
  isAtAlertLimit: boolean;
  isAtBudgetLimit: boolean;
  isBlocked: boolean;
  blockedReason: React.ReactNode;
  blockedReasonShort?: string;
  usageLimit: Required<ProfessionalMeteringInfo>['usageLimit'];
  teamID: string;
  teamName: string;
}

export const useProfessionalCreditStatus = (): ProfessionalCreditUsage => {
  const { isProfessionalEditionEnabled } = useGlobalVariables();
  const { meteringInfo } = useProfessionalEditionCreditsContext();

  const isBillingAdmin = useIsBillingAdmin();

  const {
    serviceCreditsConsumed,
    totalCreditsAllocated,
    usageLimit = {},
    plan,
    creditsConsumed,
    teamID,
    teamName
  } = meteringInfo;
  const creditsRemaining = totalCreditsAllocated - serviceCreditsConsumed;
  const usagePercentage = roundToDecimals((creditsConsumed / totalCreditsAllocated) * 100);
  const usageAlertCredits = usageLimit.alertAmount
    ? Math.round(usageLimit.alertAmount / meteringInfo.pricePerCredit)
    : Infinity;
  const usageBudgetCredits = usageLimit.budgetAmount
    ? Math.round(usageLimit.budgetAmount / meteringInfo.pricePerCredit)
    : Infinity;

  // Starter plan is out of credits if usage percentage is 100%
  const isOutOfCredits = plan === PLAN_TYPES.Starter && usagePercentage >= 100;
  // Starter plan is low on credits if usage percentage is 90%
  const isLowOnCredits = plan === PLAN_TYPES.Starter && usagePercentage >= 90;

  const isAtAlertLimit = serviceCreditsConsumed >= usageAlertCredits;
  const isAtBudgetLimit = serviceCreditsConsumed >= usageBudgetCredits;
  const isBlocked = isOutOfCredits || isAtBudgetLimit;

  const { hasReplayMode } = useReplayModeType();
  const navigate = useNavigate();

  const onNavigate = usePersistentCallback((url: string) => {
    navigate(url);
  });

  const blockedReason = useMemo(() => {
    if (!isBlocked) return null;
    return (
      <BlockedReason
        isOutOfCredits={isOutOfCredits}
        isAtBudgetLimit={isAtBudgetLimit}
        teamName={teamName}
        isBillingAdmin={isBillingAdmin}
        hasReplayMode={hasReplayMode}
        onNavigate={onNavigate}
      />
    );
  }, [isBlocked, isOutOfCredits, isAtBudgetLimit, teamName, isBillingAdmin, hasReplayMode, onNavigate]);

  const blockedReasonShort = useMemo(() => {
    if (!isBlocked) return undefined;

    if (isOutOfCredits) {
      return 'Please upgrade your plan to use this feature.';
    }
    if (isAtBudgetLimit) {
      return 'Please increase your budget to use this feature.';
    }
    return undefined;
  }, [isBlocked, isOutOfCredits, isAtBudgetLimit]);

  if (!isProfessionalEditionEnabled) {
    return {
      isOutOfCredits: false,
      isLowOnCredits: false,
      creditsConsumed: 0,
      totalCreditsAllocated: 0,
      creditsRemaining: 0,
      usagePercentage: 0,
      usageAlertCredits: 0,
      usageBudgetCredits: 0,
      isAtAlertLimit: false,
      isAtBudgetLimit: false,
      isBlocked: false,
      blockedReason: null,
      blockedReasonShort: undefined,
      usageLimit: {},
      teamID: '',
      teamName: ''
    };
  }

  return {
    isOutOfCredits,
    isLowOnCredits,
    creditsConsumed,
    totalCreditsAllocated,
    creditsRemaining,
    usagePercentage,
    usageAlertCredits,
    usageBudgetCredits,
    usageLimit,
    isAtAlertLimit,
    isAtBudgetLimit,
    isBlocked,
    blockedReason,
    blockedReasonShort,
    teamID,
    teamName
  };
};

const TooltipLink = styled.a`
  color: ${theme.colors.white};
  text-decoration: underline;
`;

export const useUpgradeToProPlan = () => {
  const openerRef = useRef<ReturnType<typeof openWindow> | null>(null);

  const upgrade = usePersistentCallback(async (teamId: string, onSuccess: () => void) => {
    openerRef.current = openWindow(
      getRedirectUri(Private_Routes.Settings.tab.getUrl({ tab: 'usage&Billing' }, { teamId })),
      {
        events: {
          'upgrade-success': () => {
            openerRef.current?.closeWindow();
            toast.success({ content: 'Upgrade successful', 'data-test-id': 'upgrade-to-pro-plan-success' });
            onSuccess();
          }
        }
      }
    );
  });

  const updateLimit = usePersistentCallback(async (teamId: string, onSuccess: () => void) => {
    openerRef.current = openWindow(
      getRedirectUri(Private_Routes.Settings.tab.getUrl({ tab: 'usage&Billing' }, { teamId, 'update-limits': 'true' })),
      {
        events: {
          'update-limit-success': () => {
            openerRef.current?.closeWindow();
            toast.success({ content: 'Update limit successful', 'data-test-id': 'update-limit-success' });
            onSuccess();
          }
        }
      }
    );
  });

  const getUpgradeLink = (teamId: string, onSuccess: () => void, text = 'Upgrade to Pro plan') => {
    return (
      <TooltipLink
        onPointerDownCapture={(e) => {
          e.preventDefault();
          e.stopPropagation();
          upgrade(teamId, onSuccess);
        }}>
        {text}
      </TooltipLink>
    );
  };

  const getUpdateLimitLink = (teamId: string, onSuccess: () => void, text = 'Update limit') => {
    return (
      <TooltipLink
        onPointerDownCapture={(e) => {
          e.preventDefault();
          e.stopPropagation();

          updateLimit(teamId, onSuccess);
        }}>
        {text}
      </TooltipLink>
    );
  };

  return { upgrade, updateLimit, openerRef, getUpgradeLink, getUpdateLimitLink };
};

export const useFetchPlanInfo = (teamId?: string) => {
  const { isProfessionalEditionEnabled } = useGlobalVariables();
  const { data, refetch, ...rest } = useRestQuery<ProfessionalMeteringInfo>(
    ['get-credit-usage', teamId],
    () => getProfessionalMeteringInfo(teamId as string),
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: Boolean(teamId) && isProfessionalEditionEnabled
    }
  );

  const refetchPlanInfo = usePersistentCallback(async () => {
    const response = await refetch();
    return response.data?.data || DEFAULT_PROFESSIONAL_METERING_INFO;
  });

  return {
    ...rest,
    refetchPlanInfo,
    planInfo: data?.data || DEFAULT_PROFESSIONAL_METERING_INFO
  };
};

export const useUpgradeButtonStatus = (
  teamId: string | undefined,
  shouldUpgrade: (planInfo: ProfessionalMeteringInfo) => boolean
) => {
  const { planInfo, refetchPlanInfo, isFetching: fetchingPlanInfo } = useFetchPlanInfo(teamId);
  const needUpgrade = useMemo(() => Boolean(teamId) && shouldUpgrade(planInfo), [shouldUpgrade, planInfo, teamId]);
  const [upgradeClicked, setUpgradeClicked] = useState(false);
  const upgradeCallbackExecuted = useRef(false);
  const { upgrade, openerRef } = useUpgradeToProPlan();

  const handleUpgrade = usePersistentCallback(async (handleSubmit: Callable) => {
    upgradeCallbackExecuted.current = false;

    // First click: open upgrade window and wait for callback
    if (teamId && needUpgrade && !upgradeClicked) {
      upgrade(teamId, async () => {
        if (!upgradeCallbackExecuted.current) {
          const response = await refetchPlanInfo();
          if (shouldUpgrade(response)) {
            setUpgradeClicked(false);
            toast.error({
              content: 'Upgrade failed. Please try again.',
              'data-test-id': 'upgrade-to-pro-plan-error'
            });
          } else {
            handleSubmit();
          }
          upgradeCallbackExecuted.current = true;
        }
      });
      setUpgradeClicked(true);
      return true;
    }

    // Second click: check if upgrade is complete
    if (needUpgrade && upgradeClicked) {
      const response = await refetchPlanInfo();
      if (shouldUpgrade(response)) {
        if (!openerRef.current?.isOpen()) {
          toast.error({
            content: 'Upgrade failed. Please try again.',
            'data-test-id': 'upgrade-to-pro-plan-error'
          });
          setUpgradeClicked(false);
        } else {
          toast.error({
            content: 'Please finish upgrading your plan to continue.',
            'data-test-id': 'upgrade-to-pro-plan-error'
          });
        }
        return true;
      }
      openerRef.current?.closeWindow();
      toast.success({
        content: 'Upgrade successful',
        'data-test-id': 'upgrade-to-pro-plan-success'
      });
    }
  });

  return {
    handleUpgrade,
    needUpgrade,
    buttonText: needUpgrade
      ? fetchingPlanInfo
        ? 'Checking...'
        : upgradeClicked
          ? 'Check upgrade status'
          : 'Upgrade Plan'
      : undefined,
    loading: needUpgrade && fetchingPlanInfo
  };
};

export const useUpdateLimitButtonStatus = (
  teamId: string | undefined,
  shouldUpdate: (planInfo: ProfessionalMeteringInfo) => boolean
) => {
  const { planInfo, refetchPlanInfo, isFetching: fetchingPlanInfo } = useFetchPlanInfo(teamId);

  const needUpdate = useMemo(() => Boolean(teamId) && shouldUpdate(planInfo), [shouldUpdate, planInfo, teamId]);
  const [updateClicked, setUpdateClicked] = useState(false);
  const updateCallbackExecuted = useRef(false);
  const { updateLimit, openerRef } = useUpgradeToProPlan();

  const handleUpdate = usePersistentCallback(async (handleSubmit: Callable) => {
    updateCallbackExecuted.current = false;

    // First click: open update limit window and wait for callback
    if (teamId && needUpdate && !updateClicked) {
      updateLimit(teamId, async () => {
        if (!updateCallbackExecuted.current) {
          const response = await refetchPlanInfo();
          if (shouldUpdate(response)) {
            setUpdateClicked(false);
            toast.error({
              content: 'Your usage still exceeds the allowed limit. Please update your limit to proceed.',
              'data-test-id': 'update-limit-error'
            });
          } else {
            handleSubmit();
          }
          updateCallbackExecuted.current = true;
        }
      });
      setUpdateClicked(true);
      return true;
    }

    // Second click: check if update is complete
    if (needUpdate && updateClicked) {
      const response = await refetchPlanInfo();
      if (shouldUpdate(response)) {
        if (!openerRef.current?.isOpen()) {
          toast.error({
            content: 'Your usage still exceeds the allowed limit. Please update your limit to proceed.',
            'data-test-id': 'update-limit-error'
          });
          setUpdateClicked(false);
        } else {
          toast.error({
            content: 'Please finish updating your limit to continue.',
            'data-test-id': 'update-limit-error'
          });
        }
        return true;
      }
      openerRef.current?.closeWindow();
      toast.success({
        content: 'Your usage limit has been updated. You can now proceed.',
        'data-test-id': 'update-limit-success'
      });
    }
  });

  return {
    handleUpdate,
    needUpdate,
    buttonText: needUpdate
      ? fetchingPlanInfo
        ? 'Checking...'
        : updateClicked
          ? 'Check limit'
          : 'Update limit'
      : undefined,
    loading: needUpdate && fetchingPlanInfo
  };
};

export const useIsBillingAdmin = () => {
  const { user } = useUser();
  const {
    meteringInfo: { userID }
  } = useProfessionalEditionCreditsContext();
  return user?._id === userID;
};
